import { createHashRouter, Navigate } from 'react-router-dom';
import { Landing } from '@/pages/public/Landing';
import { FindCarePage } from '@/pages/public/FindCarePage';
import { NotFound } from '@/pages/NotFound';
import { Placeholder } from '@/pages/Placeholder';

import { PublicLayout } from '@/layouts/PublicLayout';
import { PatientLayout } from '@/layouts/PatientLayout';
import { ClinicianLayout } from '@/layouts/ClinicianLayout';
import { PracticeLayout } from '@/layouts/PracticeLayout';
import { RegulationOpsLayout } from '@/layouts/RegulationOpsLayout';
import { AISafetyLayout } from '@/layouts/AISafetyLayout';
import { RegOpsJurisdictionsPage } from '@/pages/regops/RegOpsJurisdictionsPage';
import { RegOpsSourcesPage } from '@/pages/regops/RegOpsSourcesPage';
import { RegOpsRulesPage } from '@/pages/regops/RegOpsRulesPage';
import { RegOpsReleasesPage } from '@/pages/regops/RegOpsReleasesPage';
import { RegOpsTestCasesPage } from '@/pages/regops/RegOpsTestCasesPage';
import { RegOpsChangeRequestsPage } from '@/pages/regops/RegOpsChangeRequestsPage';
import { AISafetyModelsPage } from '@/pages/shared/AISafetyModelsPage';
import { AISafetyCasesPage } from '@/pages/shared/AISafetyCasesPage';
import { AISafetyCapabilitiesPage } from '@/pages/shared/AISafetyCapabilitiesPage';
import { AISafetyEventsPage } from '@/pages/regops/AISafetyEventsPage';

import {
  RequirePatient, RequireClinician, RequirePracticeAdmin, RequirePerm,
} from '@/auth/guards';

// Patient pages (Tour 2)
import { PatientHomePage } from '@/pages/patient/PatientHomePage';
import { PatientAppointmentsPage } from '@/pages/patient/PatientAppointmentsPage';
import { PatientAppointmentDetailPage } from '@/pages/patient/PatientAppointmentDetailPage';
import { PatientRecordPage } from '@/pages/patient/PatientRecordPage';
import { PatientPrescriptionsPage } from '@/pages/patient/PatientPrescriptionsPage';
import { PatientPrescriptionDetailPage } from '@/pages/patient/PatientPrescriptionDetailPage';
import { PatientSickNotesPage } from '@/pages/patient/PatientSickNotesPage';
import { PatientLabOrdersPage } from '@/pages/patient/PatientLabOrdersPage';
import { PatientReferralsPage } from '@/pages/patient/PatientReferralsPage';
import { PatientCarePathwaysPage } from '@/pages/patient/PatientCarePathwaysPage';
import { PatientDevicesPage } from '@/pages/patient/PatientDevicesPage';
import { PatientConsentsPage } from '@/pages/patient/PatientConsentsPage';
import { PatientWalletPage } from '@/pages/patient/PatientWalletPage';
import { PatientSubscriptionsPage } from '@/pages/patient/PatientSubscriptionsPage';
import { PatientSafetyResourcesPage } from '@/pages/patient/PatientSafetyResourcesPage';

// Clinician pages (Tour 3)
import { ClinicianTodayPage } from '@/pages/clinician/ClinicianTodayPage';
import { ClinicianAppointmentsPage } from '@/pages/clinician/ClinicianAppointmentsPage';
import { ClinicianEncounterEditorPage } from '@/pages/clinician/ClinicianEncounterEditorPage';
import { ClinicianEncountersPage } from '@/pages/clinician/ClinicianEncountersPage';
import { ClinicianAvailabilityPage } from '@/pages/clinician/ClinicianAvailabilityPage';
import { ClinicianPrescriptionsPage } from '@/pages/clinician/ClinicianPrescriptionsPage';
import { ClinicianPrescriptionComposerPage } from '@/pages/clinician/ClinicianPrescriptionComposerPage';
import { ClinicianPrescriptionDetailPage } from '@/pages/clinician/ClinicianPrescriptionDetailPage';
import { ClinicianSickNotesPage } from '@/pages/clinician/ClinicianSickNotesPage';
import { ClinicianLabOrdersPage } from '@/pages/clinician/ClinicianLabOrdersPage';
import { ClinicianReferralsPage } from '@/pages/clinician/ClinicianReferralsPage';
import { ClinicianPathwaysPage } from '@/pages/clinician/ClinicianPathwaysPage';
import { ClinicianAILogPage } from '@/pages/clinician/ClinicianAILogPage';
import { ClinicianCredentialsPage } from '@/pages/clinician/ClinicianCredentialsPage';
import { ClinicianProfilePage } from '@/pages/clinician/ClinicianProfilePage';
import { PublicClinicianPage } from '@/pages/public/PublicClinicianPage';

// Practice pages (Tour 4)
import { PracticeOverviewPage } from '@/pages/practice/PracticeOverviewPage';
import { PracticeProfilePage } from '@/pages/practice/PracticeProfilePage';
import { PracticeCliniciansPage } from '@/pages/practice/PracticeCliniciansPage';
import { PracticeClinicianDetailPage } from '@/pages/practice/PracticeClinicianDetailPage';
import { PracticeProceduresPage } from '@/pages/practice/PracticeProceduresPage';
import { PracticeIntakeTemplatesPage } from '@/pages/practice/PracticeIntakeTemplatesPage';
import { PracticeAppointmentsPage } from '@/pages/practice/PracticeAppointmentsPage';
import { PracticeReviewsPage } from '@/pages/practice/PracticeReviewsPage';
import { PracticeCarePackagesPage } from '@/pages/practice/PracticeCarePackagesPage';
import { PracticeMembershipPlansPage } from '@/pages/practice/PracticeMembershipPlansPage';
import { PracticePayerContractsPage } from '@/pages/practice/PracticePayerContractsPage';
import { PracticeInteropPage } from '@/pages/practice/PracticeInteropPage';

/**
 * Route tree.
 *
 * Layers:
 *   /                   — public marketplace + transparency (PublicLayout)
 *   /patient/*          — patient portal (PatientLayout, RequirePatient)
 *   /clinician/*        — clinician console (ClinicianLayout, RequireClinician)
 *   /practice/*         — practice admin (PracticeLayout, RequirePracticeAdmin)
 *   /regulation-ops/*   — RegulationOps (RegulationOpsLayout, perm)
 *   /ai-safety/*        — AI safety console (AISafetyLayout, perm)
 *
 * Each section's index page redirects to its dashboard so deep-linking
 * to `/patient` lands somewhere useful.
 *
 * Tour 1 ships with placeholder pages for every leaf route. Tour 2-6
 * replace the placeholders with real implementations.
 */

export const router = createHashRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/find-care', element: <FindCarePage /> },
      { path: '/clinicians/:slug', element: <PublicClinicianPage /> },
      { path: '/transparency/fairness', element: <Placeholder title="Marketplace fairness" tour={6} /> },
      { path: '/transparency/why-this-rank', element: <Placeholder title="Why this rank?" tour={6} /> },
      { path: '/transparency/ai', element: <Placeholder title="AI in care" tour={6} /> },
    ],
  },

  // ── Patient ─────────────────────────────────────────────────────────
  {
    path: '/patient',
    element: <RequirePatient><PatientLayout /></RequirePatient>,
    children: [
      { index: true, element: <PatientHomePage /> },
      { path: 'appointments', element: <PatientAppointmentsPage /> },
      { path: 'appointments/:id', element: <PatientAppointmentDetailPage /> },
      { path: 'record', element: <PatientRecordPage /> },
      { path: 'prescriptions', element: <PatientPrescriptionsPage /> },
      { path: 'prescriptions/:id', element: <PatientPrescriptionDetailPage /> },
      { path: 'sick-notes', element: <PatientSickNotesPage /> },
      { path: 'lab-orders', element: <PatientLabOrdersPage /> },
      { path: 'referrals', element: <PatientReferralsPage /> },
      { path: 'care-pathways', element: <PatientCarePathwaysPage /> },
      { path: 'devices', element: <PatientDevicesPage /> },
      { path: 'consents', element: <PatientConsentsPage /> },
      { path: 'wallet', element: <PatientWalletPage /> },
      { path: 'subscriptions', element: <PatientSubscriptionsPage /> },
      { path: 'safety-resources', element: <PatientSafetyResourcesPage /> },
    ],
  },

  // ── Clinician ────────────────────────────────────────────────────────
  {
    path: '/clinician',
    element: <RequireClinician><ClinicianLayout /></RequireClinician>,
    children: [
      { index: true, element: <ClinicianTodayPage /> },
      { path: 'appointments', element: <ClinicianAppointmentsPage /> },
      { path: 'appointments/:id', element: <ClinicianEncounterEditorPage /> },
      { path: 'availability', element: <ClinicianAvailabilityPage /> },
      { path: 'encounters', element: <ClinicianEncountersPage /> },
      { path: 'encounters/:id', element: <ClinicianEncounterEditorPage /> },
      { path: 'prescriptions', element: <ClinicianPrescriptionsPage /> },
      { path: 'prescriptions/new', element: <ClinicianPrescriptionComposerPage /> },
      { path: 'prescriptions/:id', element: <ClinicianPrescriptionDetailPage /> },
      { path: 'sick-notes', element: <ClinicianSickNotesPage /> },
      { path: 'lab-orders', element: <ClinicianLabOrdersPage /> },
      { path: 'referrals', element: <ClinicianReferralsPage /> },
      { path: 'pathways', element: <ClinicianPathwaysPage /> },
      { path: 'ai-log', element: <ClinicianAILogPage /> },
      { path: 'credentials', element: <ClinicianCredentialsPage /> },
      { path: 'me', element: <ClinicianProfilePage /> },
    ],
  },

  // ── Practice ─────────────────────────────────────────────────────────
  {
    path: '/practice',
    element: <RequirePracticeAdmin><PracticeLayout /></RequirePracticeAdmin>,
    children: [
      { index: true, element: <PracticeOverviewPage /> },
      { path: 'profile', element: <PracticeProfilePage /> },
      { path: 'clinicians', element: <PracticeCliniciansPage /> },
      { path: 'clinicians/:id', element: <PracticeClinicianDetailPage /> },
      { path: 'procedures', element: <PracticeProceduresPage /> },
      { path: 'intake-templates', element: <PracticeIntakeTemplatesPage /> },
      { path: 'appointments', element: <PracticeAppointmentsPage /> },
      { path: 'reviews', element: <PracticeReviewsPage /> },
      { path: 'care-packages', element: <PracticeCarePackagesPage /> },
      { path: 'membership-plans', element: <PracticeMembershipPlansPage /> },
      { path: 'payer-contracts', element: <PracticePayerContractsPage /> },
      { path: 'interop', element: <PracticeInteropPage /> },
    ],
  },

  // ── RegulationOps ────────────────────────────────────────────────────
  {
    path: '/regulation-ops',
    element: (
      <RequirePerm perm="medical.regulation_ops">
        <RegulationOpsLayout />
      </RequirePerm>
    ),
    children: [
      { index: true, element: <RegOpsJurisdictionsPage /> },
      { path: 'jurisdictions/:code', element: <Placeholder title="Jurisdiction detail" tour={5} /> },
      { path: 'sources', element: <RegOpsSourcesPage /> },
      { path: 'rules', element: <RegOpsRulesPage /> },
      { path: 'releases', element: <RegOpsReleasesPage /> },
      { path: 'releases/:id', element: <Placeholder title="Release detail" tour={5} /> },
      { path: 'test-cases', element: <RegOpsTestCasesPage /> },
      { path: 'change-requests', element: <RegOpsChangeRequestsPage /> },
    ],
  },

  // ── AI Safety ────────────────────────────────────────────────────────
  {
    path: '/ai-safety',
    element: (
      <RequirePerm perm="medical.ai_safety_ops">
        <AISafetyLayout />
      </RequirePerm>
    ),
    children: [
      { index: true, element: <AISafetyModelsPage /> },
      { path: 'models/:id', element: <Placeholder title="Model detail" tour={5} /> },
      { path: 'capabilities', element: <AISafetyCapabilitiesPage /> },
      { path: 'capabilities/:code', element: <Placeholder title="Capability detail" description="Live post-market stats: rejection rate, edit rate, latency." tour={5} /> },
      { path: 'safety-cases', element: <AISafetyCasesPage /> },
      { path: 'events', element: <AISafetyEventsPage /> },
    ],
  },

  // ── Misc helpers ─────────────────────────────────────────────────────
  // /me — generic redirector that sends an authenticated user to the most
  // appropriate dashboard.
  {
    path: '/me',
    element: <Navigate to="/" replace />,
    // Tour 2 will replace with smart redirect (patient -> /patient,
    // clinician -> /clinician, etc.).
  },

  { path: '*', element: <NotFound /> },
]);
